export const RURAL_SUPPORT_OPTIONS = [
  {
    id: "worn",
    icon: "↘",
    eyebrow: "Restore capacity",
    title: "I’m running on empty",
    description: "Low energy, carrying too much, or struggling to recover.",
  },
  {
    id: "disconnected",
    icon: "◎",
    eyebrow: "Gentle connection",
    title: "I feel alone or disconnected",
    description: "Distance, quiet, or limited contact is starting to feel heavy.",
  },
  {
    id: "overwhelmed",
    icon: "≋",
    eyebrow: "Reduce the load",
    title: "Everything feels like too much",
    description: "Your thoughts are racing, your body is tense, or inputs are piling up.",
  },
  {
    id: "steady",
    icon: "◇",
    eyebrow: "Preventive check-in",
    title: "I want to check in with myself",
    description: "Nothing is urgent. You want to protect what is helping.",
  },
  {
    id: "someone",
    icon: "◌",
    eyebrow: "Support another person",
    title: "I’m worried about someone",
    description: "You want to check in without judging, pushing, or trying to fix everything.",
  },
  {
    id: "resources",
    icon: "⌖",
    eyebrow: "Find support",
    title: "I need services or local help",
    description: "Start with Canada-wide services, then add supports in your own community.",
  },
];

export const RURAL_TOOLKIT_BY_FOCUS = {
  worn: {
    title: "Restore before problem-solving",
    intro:
      "When your capacity is low, pushing harder is rarely the first useful step. Start by helping your body soften, then choose one realistic recovery action.",
    orbMode: "grounding",
    tools: [
      {
        type: "grounding",
        title: "Grounding reset",
        subtitle: "Let your pace drop before deciding what comes next.",
      },
      {
        type: "body",
        title: "Micro body release",
        intro: "Take this slowly. There is nothing to get right.",
        mode: "warm",
        steps: [
          {
            text: "If it feels okay, let your shoulders drop by about 10%.",
            hint: "Not all the way. Just a little.",
            ms: 15000,
          },
          {
            text: "Let your jaw soften.",
            hint: "Your teeth do not need to touch.",
            ms: 15000,
          },
          {
            text: "Press your feet gently into the floor.",
            hint: "Notice the support underneath you.",
            ms: 15000,
          },
          {
            text: "Take one slow breath out.",
            hint: "A slightly longer exhale is enough.",
            ms: 15000,
          },
        ],
        endNote: "Even one small release can create a little more room.",
      },
      {
        type: "reflection",
        title: "Choose a recovery action",
        prompts: [
          "What has been taking the most energy lately?",
          "What could give even 5% back today?",
        ],
        actions: [
          "Drink water and eat something nourishing",
          "Protect 20 minutes of quiet",
          "Postpone one non-essential demand",
          "Ask someone for one specific form of help",
        ],
      },
    ],
  },

  disconnected: {
    title: "Reconnect without forcing a big conversation",
    intro:
      "Rural distance can reduce everyday contact. Start with presence, then make one low-pressure connection instead of waiting until you feel ready for a major conversation.",
    orbMode: "release",
    tools: [
      {
        type: "grounding",
        title: "Settling into presence",
        subtitle: "Connection can begin with simply arriving in the moment.",
      },
      {
        type: "connection",
        title: "Send one simple message",
        message:
          "Hey, I’ve had a lot on my mind lately. Would you be up for a quick call or coffee this week?",
        alternatives: [
          "Been thinking about you. How are things?",
          "I could use some company. Are you free for a short chat this week?",
          "I’m heading into town soon. Want to grab a coffee?",
        ],
      },
      {
        type: "reflection",
        title: "Build a small connection circle",
        prompts: [
          "Who feels safest or easiest to contact?",
          "Which place or activity helps me feel less alone?",
        ],
        actions: [
          "Message one person",
          "Attend part of a local event",
          "Offer or request practical help",
          "Plan one repeated weekly check-in",
        ],
      },
    ],
  },

  overwhelmed: {
    title: "Reduce inputs before solving problems",
    intro:
      "Overwhelm often means your system has too much to process at once. The aim is not to solve everything immediately—it is to lower the load enough to find one next step.",
    orbMode: "release",
    tools: [
      {
        type: "grounding",
        title: "Slow the system",
        subtitle: "Let your breath and attention move at a gentler pace.",
      },
      {
        type: "sensory",
        title: "5-4-3-2-1 grounding",
        subtitle: "Use your senses to return to what is happening right now.",
      },
      {
        type: "reflection",
        title: "One-hour control check",
        prompts: [
          "What is the loudest problem in my mind right now?",
          "What is one thing I can influence during the next hour?",
        ],
        actions: [
          "Write down the next three tasks only",
          "Turn off one source of noise or notifications",
          "Ask for more time",
          "Complete one small, concrete action",
        ],
      },
    ],
  },

  steady: {
    title: "Protect what is already working",
    intro:
      "Checking in before things become unmanageable can help you notice patterns, boundaries, and routines worth protecting.",
    orbMode: "steady",
    tools: [
      {
        type: "grounding",
        title: "Steady check-in",
        subtitle: "Notice your current state without needing to change it.",
      },
      {
        type: "reflection",
        title: "Protecting steadiness",
        prompts: [
          "What has helped me feel more stable this week?",
          "What boundary or routine do I want to protect next week?",
        ],
        actions: [
          "Keep one consistent sleep or wake time",
          "Schedule something to look forward to",
          "Plan one point of connection",
          "Leave margin between demanding tasks",
        ],
      },
    ],
  },

  someone: {
    title: "Be present, direct, and practical",
    intro:
      "You do not need to be a counsellor. A calm check-in, careful listening, and help connecting to appropriate support can make the next step easier.",
    orbMode: "steady",
    tools: [
      {
        type: "grounding",
        title: "Settle before checking in",
        subtitle: "A calmer pace can make the conversation feel safer for both of you.",
      },
      {
        type: "conversation",
        title: "Open the conversation",
        helpful: [
          "I’ve noticed you have not seemed like yourself lately. How are you doing?",
          "Thank you for telling me. That sounds difficult.",
          "You do not have to handle everything alone.",
          "What would feel most helpful right now?",
        ],
        avoid: [
          "Just think positive.",
          "Other people have it worse.",
          "You are overreacting.",
          "I know exactly how you feel.",
        ],
      },
      {
        type: "safety",
        title: "When safety may be at risk",
        content:
          "Take the concern seriously, stay present when it is safe to do so, and connect with trained support. In Canada, call or text 9-8-8. Call 9-1-1 when there is immediate danger or a medical emergency.",
      },
    ],
  },

  resources: {
    title: "Start with trusted support",
    intro:
      "Use Canada-wide services as a starting point. Community partners can add reviewed local counselling, transportation, food, housing, financial, youth, and cultural supports.",
    orbMode: "steady",
    tools: [{ type: "resources" }],
  },
};

export const RURAL_LIBRARY_SECTIONS = [
  {
    id: "stress",
    title: "Stress, burnout, and physical recovery",
    summary:
      "Recognize overload earlier and use safe movement, rest, connection, and micro-breaks to support recovery.",
    bullets: [
      "Rural stress can combine physical work, caregiving, weather, finances, isolation, and limited access to services.",
      "A low-capacity day is information, not a personal failure.",
      "During long workdays, use brief pauses only when equipment and surroundings are safe.",
      "Rest, food, water, sleep, hobbies, nature, connection, and boundaries can all support recovery.",
    ],
  },
  {
    id: "winter",
    title: "Winter and seasonal wellbeing",
    summary:
      "Build a practical plan around daylight, movement, connection, routine, and earlier support.",
    bullets: [
      "Spend time in daylight when weather and working conditions are safe.",
      "Use movement that fits your health, body, and schedule.",
      "Plan one point of contact and one thing to anticipate each week.",
      "Notice when low mood, withdrawal, or sleep changes are becoming a persistent pattern.",
    ],
  },
  {
    id: "sleep",
    title: "Sleep for rural and shift schedules",
    summary:
      "Protect sleep with a cooler, darker room, a predictable wind-down, and realistic recovery during demanding seasons.",
    bullets: [
      "Keep sleep and wake times as consistent as your schedule allows.",
      "Reduce bright screens and stimulating work close to bedtime.",
      "Use blackout curtains when daytime sleep is necessary.",
      "Persistent sleep difficulty deserves support from a qualified health professional.",
    ],
  },
  {
    id: "roles",
    title: "Support by role",
    summary: "Different pressures need different starting points.",
    groups: [
      {
        label: "Farmers and farm families",
        items: [
          "Treat financial, weather, livestock, and seasonal pressure as real stressors—not personal weakness.",
          "Use telehealth or out-of-area services when privacy is a concern.",
          "Trade practical help with neighbours during peak seasons where possible.",
        ],
      },
      {
        label: "Parents and caregivers",
        items: [
          "Listen before offering solutions.",
          "Avoid dismissing stress because of someone’s age.",
          "Practise one calming tool together instead of assigning it.",
        ],
      },
      {
        label: "Students and youth",
        items: [
          "A trusted teacher, coach, counsellor, relative, or support service can be a first step.",
          "Notice which online spaces leave you feeling supported and which leave you feeling worse.",
          "Boundaries are valid. You do not owe everyone unlimited time and energy.",
        ],
      },
    ],
  },
  {
    id: "indigenous",
    title: "Indigenous wellness and community care",
    summary:
      "Wellness may include land, culture, kinship, language, ceremony, Elders, Knowledge Keepers, and community-led support.",
    bullets: [
      "Local Nations and communities should guide local content and terminology.",
      "Indigenous wellness should not be presented as one universal approach.",
      "Partner implementations should be reviewed by appropriate local Indigenous advisors.",
      "National services can be a starting point while local cultural supports remain central.",
    ],
  },
  {
    id: "finance",
    title: "Financial pressure and practical support",
    summary:
      "Reduce uncertainty by making the problem visible, prioritizing essentials, and using qualified support.",
    bullets: [
      "List income, essential expenses, debts, and immediate deadlines.",
      "Prioritize housing, utilities, food, transportation, and safety.",
      "Use community-navigation services to locate food, housing, transportation, and financial assistance.",
      "Discuss money at a calmer time and focus on shared facts rather than blame.",
    ],
  },
  {
    id: "belonging",
    title: "Identity, belonging, and stigma",
    summary:
      "Confidential support, online connection, and trusted out-of-town services can reduce barriers in close-knit communities.",
    bullets: [
      "You do not owe neighbours or community members an explanation for seeking support.",
      "Safety and privacy should guide identity disclosure decisions.",
      "Newcomer families may need both settlement and emotional-wellness support.",
      "Community partners should add culturally and linguistically relevant services.",
    ],
  },
  {
    id: "community",
    title: "Building community connection",
    summary:
      "Small, repeated structures often create more connection than one large awareness event.",
    bullets: [
      "Monthly coffee gatherings or potlucks.",
      "Skill swaps across generations.",
      "Winter check-in trees for isolated residents.",
      "Short community walks and practical offers such as rides, errands, meals, or supplies.",
    ],
  },
  {
    id: "organizations",
    title: "For community organizations",
    summary:
      "A simple structure for facilitated wellness circles and responsible local implementation.",
    groups: [
      {
        label: "A 60–90 minute circle",
        items: [
          "Welcome and optional check-in.",
          "One focused topic from this toolkit.",
          "Practise one tool together.",
          "Community connection time.",
          "Close with one next step and support resources.",
        ],
      },
      {
        label: "Safety principles",
        items: [
          "Participation is voluntary and anyone may pass.",
          "No diagnosis, forced disclosure, or unsolicited advice.",
          "Explain confidentiality and its safety limits before the session.",
          "Facilitators need a clear escalation and referral protocol.",
        ],
      },
      {
        label: "Local launch checklist",
        items: [
          "Identify trained facilitators and backups.",
          "Review content with local clinical and cultural partners.",
          "Configure current local services and transportation options.",
          "Test the mobile experience with residents.",
          "Recheck resource links and service details regularly.",
        ],
      },
    ],
  },
];

export const RURAL_SUPPORT_RESOURCES = [
  {
    id: "988",
    category: "Immediate support",
    title: "9-8-8: Suicide Crisis Helpline",
    description: "Canada-wide support by phone or text, available at any time.",
    actions: [
      { label: "Call 9-8-8", href: "tel:988" },
      { label: "Text 9-8-8", href: "sms:988" },
      { label: "Visit 988.ca", href: "https://988.ca/" },
    ],
    featured: true,
  },
  {
    id: "kids-help-phone",
    category: "Young people",
    title: "Kids Help Phone",
    description: "Mental-health support and service navigation for young people across Canada.",
    actions: [
      { label: "Visit official site", href: "https://kidshelpphone.ca/" },
      { label: "Text CONNECT", href: "sms:686868?body=CONNECT" },
    ],
  },
  {
    id: "hope-for-wellness",
    category: "Indigenous support",
    title: "Hope for Wellness Helpline",
    description: "Phone and online support for Indigenous Peoples across Canada.",
    actions: [
      { label: "Call 1-855-242-3310", href: "tel:18552423310" },
      { label: "Visit official site", href: "https://www.hopeforwellness.ca/" },
    ],
  },
  {
    id: "farmer-wellness",
    category: "Ontario agriculture",
    title: "Farmer Wellness Initiative",
    description: "Mental-health counselling for Ontario farmers, farm workers, and farm families.",
    actions: [
      { label: "Call 1-866-267-6255", href: "tel:18662676255" },
      { label: "Visit official site", href: "https://farmerwellnessinitiative.ca/" },
    ],
  },
  {
    id: "211",
    category: "Local practical help",
    title: "211 Canada",
    description: "Find community, food, housing, transportation, financial, and other local services.",
    actions: [
      { label: "Find local services", href: "https://211.ca/" },
      { label: "Call 2-1-1", href: "tel:211" },
    ],
  },
];

export const RURAL_PRINTABLE_RESOURCES = [
  {
    id: "rural-workbook",
    accent: "orange",
    audience: "For individuals and families",
    title: "Rural Wellness Workbook",
    description:
      "Eight guided worksheets for weekly check-ins, body battery, winter wellbeing, connection, supporting someone, and creating a personal support plan.",
    pages: "8 pages",
    href: "/assets/toolkits/rural-wellness/rural-wellness-workbook.pdf",
  },
  {
    id: "quick-guide",
    accent: "purple",
    audience: "Double-sided handout",
    title: "Quick Support Guide",
    description:
      "A two-page grounding, connection, and local-support handout designed for counters, vehicles, community spaces, and take-home use.",
    pages: "2 pages",
    href: "/assets/toolkits/rural-wellness/rural-quick-support-guide.pdf",
  },
  {
    id: "facilitator-guide",
    accent: "blue",
    audience: "For organizations",
    title: "Community Wellness Circle Guide",
    description:
      "A facilitator-ready structure covering session flow, an eight-week series, confidentiality, difficult disclosures, and local implementation.",
    pages: "6 pages",
    href: "/assets/toolkits/rural-wellness/community-wellness-circle-guide.pdf",
  },
  {
    id: "general-printables",
    accent: "orange",
    audience: "Companion resource",
    title: "General Wellness Printables",
    description:
      "A co-branded collection of self-care checklists, a habit tracker, gratitude journal, day planner, 30-day challenge, prompts, affirmations, and QR resources.",
    pages: "11 pages",
    href: "/assets/toolkits/rural-wellness/general-wellness-printables-cobranded.pdf",
  },
];
